import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';

import { Form, PrimaryButton, SecondaryButton, FieldSelect, FieldTextInput } from '../../../components';

import css from './ReportListingForm.module.css';

// Keep this list (value + translation key) in sync with VALID_REASONS in
// server/api/report-listing.js - the server is the real gate, but the dropdown options and
// the server's allow-list need to describe the same set of reasons.
export const REPORT_REASONS = [
  'spam',
  'misleading_fraudulent',
  'inappropriate_content',
  'prohibited_item',
  'offensive_abusive',
  'listing_not_viewable',
  'other',
];

const ReportListingForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        handleSubmit,
        values,
        inProgress = false,
        // Named serverError, not submitError - "submitError" is react-final-form's own
        // reserved form-state field name (a getter on its internal state object), and
        // spreading a custom prop of the same name into <FinalForm {...props}> crashes with
        // "Cannot set property submitError of #<Object> which has only a getter".
        serverError,
        alreadyReported,
        onCancel,
      } = fieldRenderProps;

      const intl = useIntl();
      const classes = classNames(rootClassName || css.root, className);

      const reasonRequiredMessage = intl.formatMessage({ id: 'ReportListingForm.reasonRequired' });
      const descriptionRequiredMessage = intl.formatMessage({
        id: 'ReportListingForm.descriptionRequired',
      });
      const otherReasonRequiredMessage = intl.formatMessage({
        id: 'ReportListingForm.otherReasonRequired',
      });

      const isOtherReason = values?.reason === 'other';
      // One report per user per listing - if alreadyReported is true the outer button is
      // already disabled and won't open this modal in the first place, but guard the submit
      // here too (defense in depth against a stale mount, e.g. this stayed open across a
      // status change).
      const submitDisabled = inProgress || alreadyReported;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelect
            id="ReportListingForm.reason"
            name="reason"
            label={intl.formatMessage({ id: 'ReportListingForm.reasonLabel' })}
            validate={validators.required(reasonRequiredMessage)}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'ReportListingForm.reasonPlaceholder' })}
            </option>
            {REPORT_REASONS.map(reasonKey => (
              <option key={reasonKey} value={reasonKey}>
                {intl.formatMessage({ id: `ReportListingForm.reason.${reasonKey}` })}
              </option>
            ))}
          </FieldSelect>

          {isOtherReason ? (
            <FieldTextInput
              className={css.field}
              id="ReportListingForm.otherReasonText"
              name="otherReasonText"
              type="text"
              label={intl.formatMessage({ id: 'ReportListingForm.otherReasonLabel' })}
              placeholder={intl.formatMessage({ id: 'ReportListingForm.otherReasonPlaceholder' })}
              validate={validators.requiredAndNonEmptyString(otherReasonRequiredMessage)}
            />
          ) : null}

          <FieldTextInput
            className={css.field}
            id="ReportListingForm.description"
            name="description"
            type="textarea"
            label={intl.formatMessage({ id: 'ReportListingForm.descriptionLabel' })}
            placeholder={intl.formatMessage({ id: 'ReportListingForm.descriptionPlaceholder' })}
            validate={validators.requiredAndNonEmptyString(descriptionRequiredMessage)}
          />

          {serverError ? <p className={css.error}>{serverError}</p> : null}
          {alreadyReported ? (
            <p className={css.error}>
              <FormattedMessage id="ReportListingForm.alreadyReported" />
            </p>
          ) : null}

          <div className={css.buttonRow}>
            <SecondaryButton type="button" className={css.cancelButton} onClick={onCancel}>
              <FormattedMessage id="ReportListingForm.cancelButtonText" />
            </SecondaryButton>
            <PrimaryButton type="submit" inProgress={inProgress} disabled={submitDisabled}>
              <FormattedMessage id="ReportListingForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

export default ReportListingForm;
